import React from "react";
import { List, Typography, Space, Badge, Tooltip, Skeleton, Empty, theme, Card } from "antd";
import {
  KeyOutlined,
  GlobalOutlined,
  EnvironmentOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import { useLanguage } from "src/providers/LanguageProvider";
import type { IPasswordChange } from "src/types/types";

const { Text } = Typography;

function safeDate(iso?: string | null) {
  const dt = iso ? new Date(iso) : null;
  return dt && !Number.isNaN(dt.getTime()) ? dt : null;
}
function formatDate(iso?: string | null, locale = "tr-TR") {
  const dt = safeDate(iso);
  if (!dt) return "-";
  return dt.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
function relativeTimeFromNow(iso?: string | null, locale = "tr-TR") {
  const dt = safeDate(iso);
  if (!dt) return "-";
  const diffMs = dt.getTime() - Date.now();
  const abs = Math.abs(diffMs);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  const sec = Math.round(abs / 1000);
  if (sec < 60) return rtf.format(Math.sign(diffMs) * -sec, "second");
  const min = Math.round(sec / 60);
  if (min < 60) return rtf.format(Math.sign(diffMs) * -min, "minute");
  const hr = Math.round(min / 60);
  if (hr < 24) return rtf.format(Math.sign(diffMs) * -hr, "hour");
  const day = Math.round(hr / 24);
  if (day < 30) return rtf.format(Math.sign(diffMs) * -day, "day");
  const mon = Math.round(day / 30);
  if (mon < 12) return rtf.format(Math.sign(diffMs) * -mon, "month");
  const yr = Math.round(mon / 12);
  return rtf.format(Math.sign(diffMs) * -yr, "year");
}
function statusColor(result?: string | null) {
  const r = (result || "").toLowerCase();
  if (r === "success") return "success";
  if (r === "blocked") return "warning";
  return "error";
}

type Props = {
  items: IPasswordChange[];
  loading?: boolean;
  // opsiyonel: item'a tıklanınca
  onItemClick?: (item: IPasswordChange) => void;
  // opsiyonel: sayfalama
  pagination?: { pageSize?: number } | false;
};

const PasswordChangesList: React.FC<Props> = ({
  items,
  loading,
  onItemClick,
  pagination = { pageSize: 8 },
}) => {
  const { t } = useLanguage();
  const { token } = theme.useToken();

  if (loading) {
    return (
      <>
        <Skeleton active paragraph={{ rows: 1 }} />
        <Skeleton active paragraph={{ rows: 1 }} />
        <Skeleton active paragraph={{ rows: 1 }} />
      </>
    );
  }

  if (!items || items.length === 0) {
    return <Empty description={t("NO_PASSWORD_CHANGES", "Kayıtlı şifre değişikliği yok")} />;
  }

  return (
    <Card >
        <List
      size="small"
      itemLayout="horizontal"
      dataSource={items}
      pagination={pagination}
      split
      renderItem={(item) => {
        const whenAbs = formatDate(item.ChangedAt);
        const whenRel = relativeTimeFromNow(item.ChangedAt);
        const location = [item.City, item.Country].filter(Boolean).join(", ");

        return (
          <List.Item
            onClick={onItemClick ? () => onItemClick(item) : undefined}
            style={{
              cursor: onItemClick ? "pointer" : "default",
              paddingBlock: 8,
            }}
            actions={[
              <Tooltip key="time" title={whenAbs}>
                <Space size={6} style={{ color: token.colorTextTertiary, fontSize: 12 }}>
                  <ClockCircleOutlined />
                  <span>{whenRel}</span>
                </Space>
              </Tooltip>,
              <Badge key="status" status={statusColor(item.Result) as any} />,
            ]}
          >
            <List.Item.Meta
              avatar={
                <KeyOutlined style={{ fontSize: 18, color: token.colorTextTertiary }} />
              }
              title={
                <Space size={8} wrap>
                  <Text strong style={{ fontSize: 14 }}>
                    {t("PASSWORD_CHANGED_TITLE", "Şifre değişikliği")}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {whenAbs}
                  </Text>
                </Space>
              }
              description={
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    color: token.colorTextTertiary,
                    fontSize: 12,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <GlobalOutlined />
                    <span style={{ fontFamily: "ui-monospace, Menlo, monospace" }}>
                      {item.IpAddress || t("UNKNOWN_IP", "IP yok")}
                    </span>
                  </span>

                  {(item.City || item.Country) && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      <EnvironmentOutlined />
                      <span>{location || t("UNKNOWN_LOCATION", "Konum yok")}</span>
                    </span>
                  )}

                  {item.UserAgent && (
                    <span
                      title={item.UserAgent}
                      style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                    >
                      {item.UserAgent}
                    </span>
                  )}
                </div>
              }
            />
          </List.Item>
        );
      }}
    />
    </Card>
  );
};

export default PasswordChangesList;
